import { useState, useEffect, useMemo, ComponentType, ComponentProps, useCallback, useRef, memo } from 'react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { TeamPresenceEntity_Type } from '@weave/schema-gen-ts/dist/schemas/phone/presence/v1/api.pb';
import { DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/sip-profiles/v1/sip_profiles.pb';
import { produce } from 'immer';
import { debounce } from 'lodash-es';
import { useQuery } from 'react-query';
import SipProfileAPI, { SipProfilesApiTypes } from '@frontend/api-sip-profile';
import { UsersQueries } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import {
  useModalControl,
  Button,
  ModalControlModalProps,
  Modal,
  Tabs,
  Text,
  SpinningLoader,
  useAlert,
  Chip,
} from '@frontend/design-system';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { CardSearch } from './atoms';
import { CardGrid } from './components';
import { MAX_SELECTED_ENTITIES } from './const';
import { SelectableDeviceCard } from './device-card';
import { useStoredEntities, useTeamPresenceLocationIds } from './hooks';
import {
  UserSelectableData,
  DeviceSelectableData,
  AddCardDataLayerProps,
  CardFilters,
  AddCardDataLayerRenderProps,
  StoredEntity,
} from './types';
import { SelectableUserCard } from './user-card';

type ManageCardButtonProps = Omit<ComponentProps<typeof Button>, 'children' | 'variant'> & {
  label: string;
  variant?: 'cta' | 'page';
  initialLocationIds?: string[];
};

const EMPTY_LOCATION_IDS: string[] = [];

const transformUsersToUserSelectableData = (
  users: UserDetails[],
  getScopeName: (id: string) => string | undefined
): UserSelectableData[] => {
  return users.map((user) => ({
    id: user.userId ?? '',
    type: 'user' as const,
    name: `${user.firstName} ${user.lastName}`.trim(),
    email: user.username ?? '',
    locationIds: user.locations ?? [],
    locations: user?.locations?.map((locId) => getScopeName(locId)).filter(Boolean) ?? [],
  }));
};

const transformDevicesToDeviceSelectableData = (
  sipProfiles: SipProfilesApiTypes.SipProfileType[],
  getScopeName: (id: string) => string | undefined
): DeviceSelectableData[] => {
  return sipProfiles.map((sipProfile) => ({
    id: sipProfile?.sipProfileId ?? '',
    type: 'device' as const,
    name: sipProfile.name,
    subtitle: sipProfile?.extension?.extensionNumber
      ? i18next.t(`Ext. ${sipProfile?.extension?.extensionNumber}`, { ns: 'phone' })
      : '',
    locationIds: sipProfile.device?.location?.locationId ? [sipProfile.device.location.locationId] : [],
    locations: sipProfile.device?.location?.locationId
      ? [getScopeName(sipProfile.device.location.locationId)].filter(Boolean)
      : [],
  }));
};

export const ManageCardButton = ({ label, variant = 'page', initialLocationIds, ...rest }: ManageCardButtonProps) => {
  const { modalProps, triggerProps } = useModalControl();
  return (
    <>
      <Button
        {...triggerProps}
        variant={variant === 'cta' ? 'primary' : 'tertiary'}
        trackingId={teamPresenceTrackingId({ context: `manage-presence-button-${variant}` })}
        {...rest}
      >
        {label}
      </Button>
      <ManageCardModal modalProps={modalProps} DataLayer={AddCardDataLayer} initialLocationIds={initialLocationIds} />
    </>
  );
};

const useQueryEntities = () => {
  const { getScopeName, accessibleLocationData } = useAppScopeStore();
  const teamPresenceLocationIds = useTeamPresenceLocationIds();
  const userQuery = UsersQueries.useGetUsersInLocations(teamPresenceLocationIds);
  const deviceQuery = useQuery({
    queryKey: [teamPresenceLocationIds, 'device-data'],

    queryFn: () =>
      SipProfileAPI.ListByTenants({
        tenantIds: teamPresenceLocationIds
          .map((locationId) => accessibleLocationData[locationId]?.phoneTenantId)
          .filter((tenantId) => typeof tenantId === 'string'),
      }),
  });
  const user = getUser();

  const userSelectableData = useMemo(
    () =>
      transformUsersToUserSelectableData(
        // Filter out user's own entry
        userQuery.data?.users?.filter((u) => u.userId !== user?.userID) || [],
        getScopeName
      ) ?? [],
    [getScopeName, user?.userID, userQuery.data?.users]
  );
  const deviceSelectableData = useMemo(() => {
    const sipProfiles = deviceQuery.data?.sipProfilesByTenant
      ? Object.values(deviceQuery.data.sipProfilesByTenant)
          .flatMap((tenantData) => tenantData.sipProfiles || [])
          ?.filter(
            (profile) =>
              // Filter out user's own softphone - because it can only be tied to the user
              !(
                profile.device?.deviceType === DeviceType_Enum.SOFTPHONE &&
                profile.device.userEmailAddress === user?.username
              ) &&
              // Filter out mobile apps
              profile.device?.deviceType !== DeviceType_Enum.MOBILE_APP &&
              // Only pick sipProfiles which have a locationId which is present in teamPresenceLocationIds
              // if the location-id is missing, it means it's a single location tenant in which case FE can display all devices
              (profile.device?.location?.locationId
                ? teamPresenceLocationIds.includes(profile.device.location.locationId)
                : true)
          )
      : [];
    return transformDevicesToDeviceSelectableData(sipProfiles || [], getScopeName) ?? [];
  }, [deviceQuery.data?.sipProfilesByTenant, getScopeName, user?.username, teamPresenceLocationIds]);

  const combined = useMemo(
    () => [...userSelectableData, ...deviceSelectableData],
    [userSelectableData, deviceSelectableData]
  );

  if (userQuery.status === 'success' && deviceQuery.status === 'success') {
    return {
      users: userSelectableData,
      devices: deviceSelectableData,
      combined,
      isLoading: false,
    };
  }

  return {
    users: [],
    devices: [],
    combined: [],
    isLoading: userQuery.isLoading || deviceQuery.isLoading,
  };
};

const useTeamPresenceLocationList = () => {
  const { getScopeName } = useAppScopeStore();
  const teamPresenceLocationIds = useTeamPresenceLocationIds();

  return useMemo(
    () =>
      teamPresenceLocationIds.map((locationId) => ({
        value: locationId,
        label: getScopeName(locationId) || '',
      })),
    [teamPresenceLocationIds, getScopeName]
  );
};

export const AddCardDataLayer = memo(({ filters, render }: AddCardDataLayerProps) => {
  const { users, devices, combined, isLoading } = useQueryEntities();
  const { entities } = useStoredEntities();
  const locationList = useTeamPresenceLocationList();

  const filteredList = useMemo(() => {
    const searchLower = filters.search.toLowerCase();
    const hasLocationFilter = filters.location.length > 0;

    const matchesFilters = (locations: string[], ...searchFields: string[]) => {
      const locationMatch = !hasLocationFilter || locations.some((location) => filters.location.includes(location));
      const searchMatch = !filters.search || searchFields.some((field) => field.toLowerCase().includes(searchLower));
      return locationMatch && searchMatch;
    };

    return {
      users: users.filter((user) => matchesFilters(user.locationIds, user.name, user.email)),
      devices: devices.filter((device) => matchesFilters(device.locationIds, device.name, device.subtitle)),
      selected: entities
        .map((entity) => combined.find((u) => u.id === entity.id))
        .filter((entity): entity is UserSelectableData | DeviceSelectableData => entity !== undefined),
    };
  }, [filters, users, devices, combined, entities]);

  return render({ filteredList, locationList, filters, isLoading });
});

AddCardDataLayer.displayName = 'AddCardDataLayer';

const useManageCardsModal = (onClose: () => void, initialLocationIds?: string[], isOpen = false) => {
  const normalizedInitialLocationIds = initialLocationIds ?? EMPTY_LOCATION_IDS;
  const wasOpenRef = useRef(false);
  const [filters, setFilters] = useState<Pick<CardFilters, 'location' | 'search'>>({
    location: normalizedInitialLocationIds,
    search: '',
  });
  const alerts = useAlert();
  const { t } = useTranslation('phone');

  const [selectedList, setSelectedList] = useState<{
    users: UserSelectableData[];
    devices: DeviceSelectableData[];
  }>({
    users: [],
    devices: [],
  });

  const [initialList, setInitialList] = useState<{
    users: UserSelectableData[];
    devices: DeviceSelectableData[];
  }>({
    users: [],
    devices: [],
  });

  const { addEntities, replaceEntities, entities } = useStoredEntities();
  const hasExistingEntities = entities.length > 0;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setFilters({
        location: normalizedInitialLocationIds,
        search: '',
      });
    }

    wasOpenRef.current = isOpen;
  }, [normalizedInitialLocationIds, isOpen]);

  const handleFilterChange = useCallback(
    <K extends keyof Pick<CardFilters, 'location' | 'search'>>(
      field: K,
      value: Pick<CardFilters, 'location' | 'search'>[K]
    ) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetSelection = useCallback(() => {
    setSelectedList(initialList);
  }, [initialList]);

  const handleSave = useCallback(() => {
    const newEntities: Omit<StoredEntity, 'addedAt'>[] = [
      ...selectedList.users.map((user) => ({
        id: user.id,
        type: TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_USER,
        locationIds: user.locationIds,
      })),
      ...selectedList.devices.map((device) => ({
        id: device.id,
        type: TeamPresenceEntity_Type.TEAM_PRESENCE_TYPE_DEVICE,
        locationIds: device.locationIds,
      })),
    ];

    try {
      if (hasExistingEntities) {
        // replaceEntities handles adding addedAt for new entities and preserving it for existing ones
        replaceEntities(newEntities);
      } else {
        addEntities(newEntities);
      }
      alerts.success({ message: t('Team Status updated.') });
      pendo.track(teamPresenceTrackingId({ context: 'manage-presence-modal::save-event' }), {
        selectedCount: newEntities.length,
        users: selectedList.users.length,
        devices: selectedList.devices.length,
        action: hasExistingEntities ? 'updated' : 'added',
      });

      onClose();
      handleFilterChange('search', '');
      resetSelection();
    } catch (error) {
      alerts.error({ message: t('Failed to update team status. Please try again.') });
    }
  }, [
    selectedList.users,
    selectedList.devices,
    hasExistingEntities,
    alerts,
    t,
    onClose,
    handleFilterChange,
    resetSelection,
    replaceEntities,
    addEntities,
  ]);

  const handleClose = useCallback(() => {
    onClose();
    handleFilterChange('search', '');
    resetSelection();
  }, [onClose, handleFilterChange, resetSelection]);

  const handleCardSelect = useCallback((item: UserSelectableData | DeviceSelectableData, selected: boolean) => {
    setSelectedList((prev) =>
      produce(prev, (draft) => {
        if (selected) {
          if (item.type === 'user') {
            draft.users.push(item);
          } else {
            draft.devices.push(item);
          }
        } else {
          if (item.type === 'user') {
            const index = draft.users.findIndex((user) => user.id === item.id);
            if (index > -1) draft.users.splice(index, 1);
          } else {
            const index = draft.devices.findIndex((device) => device.id === item.id);
            if (index > -1) draft.devices.splice(index, 1);
          }
        }
      })
    );
  }, []);

  const initializeSelection = useCallback((selected: (UserSelectableData | DeviceSelectableData)[]) => {
    setSelectedList({
      users: selected.filter((item): item is UserSelectableData => item.type === 'user'),
      devices: selected.filter((item): item is DeviceSelectableData => item.type === 'device'),
    });
    setInitialList({
      users: selected.filter((item): item is UserSelectableData => item.type === 'user'),
      devices: selected.filter((item): item is DeviceSelectableData => item.type === 'device'),
    });
  }, []);

  const selectedCount = selectedList.users.length + selectedList.devices.length;
  const isLimitReached = selectedCount >= MAX_SELECTED_ENTITIES;

  return {
    filters,
    selectedList,
    selectedCount,
    isLimitReached,
    hasExistingEntities,
    handleFilterChange,
    handleSave,
    handleClose,
    handleCardSelect,
    initializeSelection,
    resetSelection,
  };
};

export const ManageCardModal = ({
  modalProps,
  DataLayer,
  initialLocationIds,
}: {
  modalProps: ModalControlModalProps;
  DataLayer: ComponentType<AddCardDataLayerProps>;
  initialLocationIds?: string[];
}) => {
  const { t } = useTranslation('phone');
  const modal = useManageCardsModal(modalProps.onClose, initialLocationIds, modalProps.show);
  const locationList = useTeamPresenceLocationList();

  return (
    // width accommodates padding
    <Modal {...modalProps} minWidth={600} maxWidth={600} css={{ height: 600 }} onClose={modal.handleClose}>
      <Modal.Header onClose={modal.handleClose}>{t('Manage Users and Devices')}</Modal.Header>
      <Modal.Label>
        <LocationFilterChip filters={modal.filters} locationList={locationList} />
      </Modal.Label>
      <Modal.Body css={{ gridTemplateRows: 'auto auto auto 1fr' }}>
        <Text>{t('Select the Users and Devices whose activity you would like to monitor.')}</Text>
        <DataLayer
          filters={modal.filters}
          render={({ filteredList, isLoading }) => {
            return (
              <>
                <SimpleModalContent
                  filteredList={filteredList}
                  locationList={locationList}
                  selectedList={modal.selectedList}
                  selectedCount={modal.selectedCount}
                  isLimitReached={modal.isLimitReached}
                  filters={modal.filters}
                  handleFilterChange={modal.handleFilterChange}
                  handleCardSelect={modal.handleCardSelect}
                  initializeSelection={modal.initializeSelection}
                  isLoading={isLoading}
                />
              </>
            );
          }}
        />
      </Modal.Body>
      <Modal.Footer
        helperText={t('Selected ({{count}}/{{max}})', { count: modal.selectedCount, max: MAX_SELECTED_ENTITIES })}
        secondary={{
          label: t('Cancel'),
          onClick: modal.handleClose,
        }}
        primary={{
          label: modal.hasExistingEntities ? t('Update') : t('Add'),
          disabled: modal.selectedCount > MAX_SELECTED_ENTITIES,
          onClick: modal.handleSave,
          trackingId: teamPresenceTrackingId({ context: 'manage-presence-modal::save-button' }),
        }}
      />
    </Modal>
  );
};

type ModalState = ReturnType<typeof useManageCardsModal>;

const LocationFilterChip = ({
  filters,
  locationList,
}: {
  filters: Pick<CardFilters, 'location' | 'search'>;
  locationList: AddCardDataLayerRenderProps['locationList'];
}) => {
  const { t } = useTranslation('phone');

  if (filters.location.length > 1) {
    return (
      <Chip
        variant='default'
        leftElement={<Icon name='locations' size={16} />}
        tooltipLabel={
          <div style={{ display: 'grid', gap: 'var(--fabric-space-gap-sm)' }}>
            <p>
              {t('{{count}} locations selected in your dashboard Location filter.', {
                count: filters.location.length,
              })}
            </p>
            <p>{t('Update the Location filter to select Users or Devices from other locations.')}</p>
          </div>
        }
      >
        {t('{{count}} Locations', { count: filters.location.length })}
      </Chip>
    );
  }

  if (filters.location.length === 1) {
    const selectedLocationLabel = locationList.find((loc) => loc.value === filters.location[0])?.label || '';

    return (
      <Chip.Location
        variant='default'
        tooltipLabel={
          <div style={{ display: 'grid', gap: 'var(--fabric-space-gap-sm)' }}>
            <p>
              {t('{{location}} is selected in your dashboard Location filter.', {
                location: selectedLocationLabel,
              })}
            </p>
            <p>{t('Update the Location filter to select Users or Devices from other locations.')}</p>
          </div>
        }
      >
        {selectedLocationLabel}
      </Chip.Location>
    );
  }

  return null;
};

const SimpleModalContent = memo(
  ({
    filteredList,
    locationList,
    selectedList,
    selectedCount,
    isLimitReached,
    // filters,
    handleFilterChange,
    handleCardSelect,
    initializeSelection,
    isLoading,
  }: {
    filteredList: AddCardDataLayerRenderProps['filteredList'];
    locationList: AddCardDataLayerRenderProps['locationList'];
    selectedList: ModalState['selectedList'];
    selectedCount: number;
    isLimitReached: boolean;
    filters: ModalState['filters'];
    handleFilterChange: ModalState['handleFilterChange'];
    handleCardSelect: ModalState['handleCardSelect'];
    initializeSelection: ModalState['initializeSelection'];
    isLoading: boolean;
  }) => {
    const { t } = useTranslation('phone');
    const prevSelectedRef = useRef<(UserSelectableData | DeviceSelectableData)[]>([]);

    // Initialize selection when filteredList.selected changes
    useEffect(() => {
      const selectedChanged =
        filteredList.selected.length !== prevSelectedRef.current.length ||
        filteredList.selected.some((item, index) => item.id !== prevSelectedRef.current[index]?.id);

      if (selectedChanged) {
        initializeSelection(filteredList.selected);
        prevSelectedRef.current = filteredList.selected;
      }

      // scroll to the top of the panels
      const panel = document.querySelector('.manage-presence-tab-panel[role="tabpanel"]');
      if (panel) {
        panel.scrollTop = 0;
      }
    }, [filteredList.selected, initializeSelection]);

    const debouncedSearchChange = debounce((value) => {
      handleFilterChange('search', value);
    }, 200);

    return (
      <>
        <div css={{ display: 'grid' }}>
          <CardSearch onChange={debouncedSearchChange} />
        </div>
        <Tabs initialTab='users'>
          <Tabs.Bar>
            <Tabs.Tab id='users'>{t('Users')}</Tabs.Tab>
            <Tabs.Tab id='devices'>{t('Devices')}</Tabs.Tab>
            <Tabs.Tab id='selected'>
              {isLimitReached
                ? t('Currently Selected ({{count}}, Maximum)', { count: selectedCount })
                : t('Currently Selected ({{count}})', { count: selectedCount })}
            </Tabs.Tab>
          </Tabs.Bar>
          <Tabs.Panel controller='users' className='manage-presence-tab-panel' css={{ overflowY: 'auto' }}>
            {isLoading && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <SpinningLoader />
              </div>
            )}
            {!isLoading && filteredList.users.length === 0 && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <Text>{t('Nothing to show')}</Text>
              </div>
            )}
            {!isLoading && filteredList.users.length > 0 && (
              <div
                css={{
                  containerType: 'inline-size',
                  containerName: 'user-select',
                }}
              >
                <CardGrid
                  list={filteredList.users}
                  cellWidth={258}
                  renderer={(user) => (
                    <SelectableUserCard
                      isSelected={selectedList.users.some((selectedUser) => selectedUser.id === user.id)}
                      key={user.email}
                      {...user}
                      // searchTerm={filters.search}
                      onChange={(selected) => handleCardSelect(user, selected)}
                      showLocations={locationList.length > 1}
                      disabled={isLimitReached && !selectedList.users.some((u) => u.id === user.id)}
                      disabledTooltip={t('Deselect a selected user or device to select this user or device.')}
                    />
                  )}
                />
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel controller='devices' className='manage-presence-tab-panel' css={{ overflowY: 'auto' }}>
            {isLoading && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <SpinningLoader />
              </div>
            )}
            {!isLoading && filteredList.devices.length === 0 && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <Text>{t('Nothing to show')}</Text>
              </div>
            )}
            {!isLoading && filteredList.devices.length > 0 && (
              <div
                css={{
                  containerType: 'inline-size',
                  containerName: 'device-select',
                }}
              >
                <CardGrid
                  list={filteredList.devices}
                  cellWidth={258}
                  renderer={(device) => (
                    <SelectableDeviceCard
                      isSelected={selectedList.devices.some(
                        (selectedDevice: DeviceSelectableData) => selectedDevice.id === device.id
                      )}
                      key={device.name}
                      {...device}
                      onChange={(selected) => handleCardSelect(device, selected)}
                      // searchTerm={filters.search}
                      showLocations={locationList.length > 1}
                      disabled={isLimitReached && !selectedList.devices.some((d) => d.id === device.id)}
                      disabledTooltip={t('Deselect a selected user or device to select this user or device.')}
                    />
                  )}
                />
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel controller='selected' className='manage-presence-tab-panel' css={{ overflowY: 'auto' }}>
            {isLoading && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <SpinningLoader />
              </div>
            )}
            {!isLoading && selectedCount === 0 && (
              <div css={{ display: 'grid', placeItems: 'center', height: 200 }}>
                <Text>{t('Nothing selected')}</Text>
              </div>
            )}
            {!isLoading && selectedCount > 0 && (
              <div
                css={{
                  containerType: 'inline-size',
                  containerName: 'selected-items',
                }}
              >
                <CardGrid
                  list={[...selectedList.users, ...selectedList.devices]}
                  cellWidth={258}
                  renderer={(entity) =>
                    entity.type === 'user' ? (
                      <SelectableUserCard
                        isSelected={true}
                        key={entity.email}
                        {...entity}
                        // searchTerm={filters.search}
                        onChange={(selected) => handleCardSelect(entity, selected)}
                        showLocations={locationList.length > 1}
                      />
                    ) : (
                      <SelectableDeviceCard
                        isSelected={true}
                        key={entity.name}
                        {...entity}
                        onChange={(selected) => handleCardSelect(entity, selected)}
                        // searchTerm={filters.search}
                        showLocations={locationList.length > 1}
                      />
                    )
                  }
                />
              </div>
            )}
          </Tabs.Panel>
        </Tabs>
      </>
    );
  }
);

SimpleModalContent.displayName = 'SimpleModalContent';
